import React, { useEffect, useMemo } from 'react';
import { EditorContent as EditorContentTiptap, useEditor } from '@tiptap/react';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { Doc } from 'yjs';
import {
  addIsSelfProperty,
  getEditorProps,
} from 'app/components/Song/Lyrics/TextEditor/utils';
import { MenuBar } from 'app/components/Song/Lyrics/MenuBar';
import { Box, chakra, Fade, useMultiStyleConfig } from '@chakra-ui/react';
import {
  useCharLimit,
  useEditorStatus,
  useShowEditor,
} from 'app/components/Song/Lyrics/TextEditor/hooks';
import { Lyrics2Configuration } from 'types/lyrics.types';
import { lyricsEditAction } from 'app/store/SongList/slice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getLyricsEditorExtensions } from 'app/components/Song/Lyrics/Extensions/utils';
import {
  transformPastedHTML,
  formatCopyClipboard,
} from '@session-clients/lyrics-extensions';
import { setCoWriterBarSettings } from 'app/store/Settings/slice';
import { getCoWriterBarSettings } from 'app/store/Settings/selectors';

const EditorContent = chakra(EditorContentTiptap);

interface TextEditorProps {
  yDoc: Doc;
  provider: HocuspocusProvider;
  config: Lyrics2Configuration;
  onDownload: () => void;
  isDisabledDownloadButton: boolean;
  readonly?: boolean;
  emptyElement?: React.ReactNode;
}

export const TextEditor: React.FC<TextEditorProps> = ({
  provider,
  yDoc,
  config,
  onDownload,
  isDisabledDownloadButton,
  readonly = false,
  emptyElement,
}) => {
  const dispatch = useAppDispatch();
  const type = useAppSelector(getCoWriterBarSettings)?.type;
  const styles = useMultiStyleConfig('TextEditor', {});
  const showEditor = useShowEditor();
  const { isConnected } = useEditorStatus(provider);

  const extensions = useMemo(
    () =>
      getLyricsEditorExtensions({
        config,
        provider,
        yDoc,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provider, yDoc, config]
  );

  const editor = useEditor(
    {
      extensions,
      editorProps: { transformPastedHTML },
      onBlur: ({ editor }) => {
        if (type) {
          dispatch(setCoWriterBarSettings({ type, lyrics: editor.getText() }));
        }
      },
    },
    [extensions]
  );

  useEffect(() => {
    editor?.on('focus', () => {
      dispatch(lyricsEditAction(null, editor?.isEmpty));
    });
  }, [editor, dispatch]);

  const users = addIsSelfProperty(
    editor?.storage?.collaborationCursor?.users || [],
    yDoc.clientID
  );

  useCharLimit(editor, config);

  return (
    <>
      {editor && !readonly && (
        <MenuBar
          disabled={!isConnected}
          editor={editor}
          config={config}
          users={users}
          onDownloadClick={onDownload}
          isDisabledDownloadButton={isDisabledDownloadButton || editor.isEmpty}
        />
      )}
      <Box __css={styles.root}>
        {!editor?.getText() && emptyElement
          ? emptyElement
          : showEditor && (
              <Fade in={showEditor}>
                <EditorContent
                  {...getEditorProps({ isEditable: !readonly && isConnected })}
                  editor={editor}
                  __css={{
                    ...styles.editorContent,
                    ...styles.text,
                  }}
                  onCopy={(event) => {
                    formatCopyClipboard((event as unknown) as ClipboardEvent);
                  }}
                />
              </Fade>
            )}
      </Box>
    </>
  );
};
